import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Cpu, Layers, Palette, Leaf, Download, ArrowRight, Globe, Wrench, Factory, Droplets } from "lucide-react";
import { SiFacebook, SiLinkedin, SiYoutube, SiInstagram } from "react-icons/si";
import { ROUTE_PATHS } from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
interface LayoutEnProps {
  children: React.ReactNode;
}
export function LayoutEn({
  children
}: LayoutEnProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);




  const productionItemsEn = [{
    title: "Foundry",
    href: `${ROUTE_PATHS.PRODUCTION_EN}#foundry`,
    icon: <Cpu className="h-5 w-5" />,
    desc: "High-quality aluminum billet production."
  }, {
    title: "Mold Manufacturing",
    href: `${ROUTE_PATHS.PRODUCTION_EN}#mold-manufacturing`,
    icon: <Layers className="h-5 w-5" />,
    desc: "Extrusion mold design and manufacturing services."
  }, {
    title: "Extrusion",
    href: `${ROUTE_PATHS.PRODUCTION_EN}#extrusion`,
    icon: <Cpu className="h-5 w-5" />,
    desc: "4\" - 8\" wide extrusion line capacity."
  }, {
    title: "Surface Treatment",
    href: `${ROUTE_PATHS.PRODUCTION_EN}#surface-treatment`,
    icon: <Palette className="h-5 w-5" />,
    desc: "Anodizing and electrostatic powder coating facilities."
  }, {
    title: "Mechanical Processing",
    href: `${ROUTE_PATHS.PRODUCTION_EN}#mechanical`,
    icon: <Layers className="h-5 w-5" />,
    desc: "Precision cutting, drilling and CNC machining solutions."
  }];
  const productItemsEn = [{
    title: "Architectural Systems",
    href: ROUTE_PATHS.ARCHITECTURAL_SYSTEMS_EN,
    desc: "Aesthetic solutions for window, door and facade systems."
  }, {
    title: "Industrial Profiles",
    href: ROUTE_PATHS.INDUSTRIAL_PROFILES_EN,
    desc: "Special alloy profiles for automotive, energy and machinery sectors."
  }, {
    title: "Facade Systems",
    href: ROUTE_PATHS.FACADE_SYSTEMS_EN,
    desc: "Aesthetic facade cladding and joinery solutions for modern buildings."
  }];
  return <div className="flex min-h-screen flex-col">
      {/* Top Bar */}
      <div className="hidden lg:block py-2 text-primary-foreground bg-[rgb(29,46,105)]">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs">
          <div className="flex gap-6">
            <span className="flex items-center gap-1.5 font-bold text-[rgb(255,255,255)]">
              <Mail className="h-3.5 w-3.5" /> info@mimalum.com
            </span>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Link to="/tr" className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)] hover:opacity-100 opacity-70 transition-opacity">TR</Link>
              <span className="opacity-50 text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">|</span>
              <span className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">EN</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b bg-white", isScrolled ? "shadow-md" : "")}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to={ROUTE_PATHS.HOME_EN} className="flex items-center -ml-4">
            <img src={IMAGES.LOGO_276} alt="Company Logo" className="h-[100px] object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.HOME_EN}>Home</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.COMPANY_EN}>About</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Products</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 p-6 w-[400px]">
                      {productItemsEn.map(item => <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <Link to={item.href} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                              <div className="text-sm font-semibold leading-none">{item.title}</div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                                {item.desc}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.ALUMINUM_BILLET_EN}>Aluminum Billet / Ingot</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Production</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {productionItemsEn.map(item => <li key={item.title}>
                          <NavigationMenuLink asChild>
                            <Link to={item.href} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                              <div className="flex items-center gap-2">
                                {item.icon}
                                <div className="text-sm font-medium leading-none">{item.title}</div>
                              </div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-2">
                                {item.desc}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.SUSTAINABILITY_EN}>Sustainability</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.PROFILE_GUIDE_EN} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Profile Guide</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to={ROUTE_PATHS.CONTACT_EN}>Contact</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>


          </div>

          {/* Quote Button */}
          <div className="hidden lg:block">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2 font-semibold">
              <Link to={`${ROUTE_PATHS.CONTACT_EN}#quote-form`}>Request Quote</Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto max-h-screen">
                <SheetHeader>
                  <SheetTitle>Navigation</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  <NavLink to={ROUTE_PATHS.HOME_EN} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Home</NavLink>
                  <NavLink to={ROUTE_PATHS.COMPANY_EN} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>About</NavLink>
                  
                  <div className="space-y-2">
                    <div className="text-lg font-medium">Products</div>
                    <div className="pl-4 space-y-2">
                      {productItemsEn.map(item => <Link key={item.href} to={item.href} className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                          {item.title}
                        </Link>)}
                    </div>
                  </div>

                  <NavLink to={ROUTE_PATHS.ALUMINUM_BILLET_EN} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Aluminum Billet / Ingot</NavLink>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground uppercase tracking-widest font-bold">Production</span>
                    {productionItemsEn.map(item => <Link key={item.title} to={item.href} className="pl-4 text-md py-1" onClick={() => setIsSheetOpen(false)}>{item.title}</Link>)}
                  </div>

                  <NavLink to={ROUTE_PATHS.SUSTAINABILITY_EN} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Sustainability</NavLink>
                  <NavLink to={ROUTE_PATHS.PROFILE_GUIDE_EN} className="text-lg font-medium" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setIsSheetOpen(false); }}>Profile Guide</NavLink>
                  <NavLink to={ROUTE_PATHS.CONTACT_EN} className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Contact</NavLink>

                  <div className="pt-4 border-t">
                    <div className="text-sm font-medium mb-2">Language</div>
                    <div className="flex gap-2">
                      <Link
                        to="/tr"
                        className="px-3 py-1 rounded text-sm bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground"
                        onClick={() => setIsSheetOpen(false)}
                      >
                        Türkçe
                      </Link>
                      <span className="px-3 py-1 rounded text-sm bg-primary text-primary-foreground">
                        English
                      </span>
                    </div>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="space-y-6">
              <Link to={ROUTE_PATHS.HOME_EN} className="flex items-center">
                <div className="bg-white text-primary p-2 rounded">
                  <img src={IMAGES.LOGO_276} alt="Logo" className="h-12 w-auto object-contain" />
                </div>
              </Link>
              <p className="text-sm text-primary-foreground/70 leading-relaxed">
                Founded in 2020, we produce high-quality aluminum solutions in our integrated production facilities.
                We aim for industrial excellence in every stage from casting to mechanical processing.
              </p>
              <div className="flex gap-4">
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiLinkedin className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/mimalum.aluminum" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiInstagram className="h-5 w-5" />
                </a>
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiFacebook className="h-5 w-5" />
                </a>
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full">
                  <SiYoutube className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Quick Access</h3>
              <ul className="space-y-4 text-sm text-primary-foreground/70">
                <li><Link to={ROUTE_PATHS.COMPANY_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> About</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Production Facilities</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTS_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Product Catalogs</Link></li>
                <li><Link to={ROUTE_PATHS.SUSTAINABILITY_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Sustainability</Link></li>
                <li><Link to={ROUTE_PATHS.DOWNLOAD_CENTER_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Our Certificates</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Production Units</h3>
              <ul className="space-y-4 text-sm text-primary-foreground/70">
                <li><Link to={ROUTE_PATHS.PRODUCTION_FOUNDRY_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Aluminum Casting</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_EXTRUSION_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Profile Extrusion</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_COATING_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Surface Coating</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTION_MECHANICAL_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Mechanical Processing</Link></li>
                <li><Link to={ROUTE_PATHS.PRODUCTS_INDUSTRIAL_EN} className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Industrial Solutions</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Contact Us</h3>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div className="text-primary-foreground/70 space-y-2">
                    <div>
                      <p className="font-semibold text-primary-foreground/90 text-xs uppercase tracking-wider mb-0.5">Head Office</p>
                      <p>Zuhuratbaba Mah. Gençler Cad. NO:58/1 <br />Bakırköy, İSTANBUL</p>
                    </div>
                    <div>
                      <p className="font-semibold text-primary-foreground/90 text-xs uppercase tracking-wider mb-0.5">Factory</p>
                      <p>Pelitli Mah. Tavşanlı Yolu Cad. No:123 <br />Gebze / KOCAELİ</p>
                    </div>
                    <div>
                      <p className="font-semibold text-primary-foreground/90 text-xs uppercase tracking-wider mb-0.5">USA Office</p>
                      <p>361 South Bundy Drive <br />Los Angeles, CA 90049</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Phone className="h-5 w-5 text-accent shrink-0" />
                  <div className="text-primary-foreground/70">
                    <p>Export: +90 212 671 28 70</p>
                    <p>Information: +90 532 585 08 50</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Mail className="h-5 w-5 text-accent shrink-0" />
                  <p className="text-primary-foreground/70">info@mimalum.com</p>
                </div>
              </div>
              <Button asChild variant="secondary" className="w-full font-bold">
                <Link to={ROUTE_PATHS.CONTACT_EN}>Send Us a Message</Link>
              </Button>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-primary-foreground/50">
            <p>© 2026 Mimhas Intelligence. All Rights Reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Use</a>
              <a href="#" className="hover:text-white transition-colors">Data Protection</a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}